/**
 * Реєстр сторінок розділу «Адміністрування» (ADM-801; layout.md, розд. 6): частина для ПЕРШОГО
 * ЕКРАНА — лише доступ. Два джерела:
 * - сторінки платформи (редактори E1a, люди й права E2) — кожна у своїй теці `pages/<id>/`: id
 *   сторінки — назва теки; `access.ts` (група й право: `AdminPageAccessRule`) їде в перший екран,
 *   `page.ts` (назва, опис, порядок, лінивий `load`: `PlatformAdminPageBody`) — лише в розділ.
 *   Реєстр збирає теки під час збірки, тож паралельні тікети не правлять спільного файла;
 * - сторінки модулів — слот `adminPages` вебманіфесту (`@clientline/contracts/web-module`),
 *   лише увімкнених модулів.
 *
 * Першому екрану потрібно одне: чи є в людини хоч одна сторінка (пункт «Адміністрування» в меню
 * і стартовий маршрут) — тож тут тільки право, ні React, ні стилів, ні текстів. Тексти, порядок і
 * сторінка людини (`AdminPage`) — у `catalog.ts`, який вантажить лише розділ. Код сторінки
 * вантажиться, коли її відкрили. Без права сторінки немає ні в меню, ні в пошуку, ні за прямою
 * адресою (П-05); право в інтерфейсі лише ховає, дію перевіряє сервер.
 */
import type { AppMeta } from '@clientline/contracts';
import {
  ADMIN_GROUPS,
  type AdminGroup,
  type LazyComponent,
  type WebModuleManifest,
} from '@clientline/contracts/web-module';
import type { PerfMark } from '../../perf/marks.ts';
import { WEB_MODULES } from '../../platform/modules.ts';

export { ADMIN_GROUPS, type AdminGroup };

/** Право, будь-яке з переліку, або перевірка за метаданими людини (без React, перший екран). */
export type AdminPageAccess = string | readonly string[] | ((meta: AppMeta) => boolean);

/** Те, за чим сторінка потрапляє людині: id, група й право. */
export interface AdminPageGate {
  /** Сегмент адреси `/admin/<id>`: латиниця, цифри, `-`, `_` (без крапки — вона в модулів). */
  id: string;
  group: AdminGroup;
  /**
   * Право, без якого сторінки не видно (каталог прав, permissions.md). Перелік — будь-яке з прав:
   * сторінка потрібна людям з різними правами (центр фонових операцій — `jobs.manage` для всіх
   * задач і автори задач без нього, які бачать лише свої). Функція — сторінка без окремого права:
   * доступ дають права на сутності з `/meta` (довідники ведуть люди з правом на сутність, ADM-204).
   */
  permission: AdminPageAccess;
}

/** `pages/<id>/access.ts`: група й право; id — назва теки. Лише типи й дані (перший екран). */
export type AdminPageAccessRule = Omit<AdminPageGate, 'id'>;

/** Назва, опис і ключові слова сторінки: меню розділу, головна адмінки, пошук. */
export interface AdminPageText {
  /** Підпис у меню розділу — з простору імен словника цієї сторінки. */
  label: string;
  /** Одне речення на головній адмінки. */
  description?: string;
  /** Ключові слова й синоніми для пошуку. */
  keywords?: readonly string[];
}

/** `pages/<id>/page.ts`: тексти, порядок і лінивий код сторінки (лише розділ, не перший екран). */
export interface PlatformAdminPageBody extends AdminPageText {
  /**
   * Порядок у групі (менше — вище); без нього — 100. Сторінки з однаковим порядком ідуть за
   * назвою теки, а сторінки модулів — після сторінок платформи.
   */
  order?: number;
  /**
   * Сторінка сама забирає `/` і `J` / `K` (власний пошук і таблиця): чип `/` стоїть у її пошуку,
   * а не в пошуку розділів, шпаргалка показує клавіші там же, а меню розділу тоді — через `Tab`
   * (keyboard.md, розд. 9). Сторінки модулів клавіш не забирають.
   */
  takesKeys?: boolean;
  /** Мітка початку заміру «від кліку по пункту меню» (бюджет сторінки в performance-budget). */
  startMark?: PerfMark;
  load: LazyComponent;
}

/** Сторінка цілком (доступ + тексти й код): так її бачить каталог; у першому екрані повної немає. */
export type PlatformAdminPage = AdminPageGate & PlatformAdminPageBody;

export const PLATFORM_PAGE_ID = /^[a-z][a-z0-9_-]{0,39}$/;

const accessRules = import.meta.glob<AdminPageAccessRule>('./pages/*/access.ts', {
  eager: true,
  import: 'default',
});

/** Назва теки з `./pages/<тека>/access.ts` — це id сторінки. */
export const pageDir = (path: string) => path.split('/')[2] ?? '';

const PLATFORM_PAGES: readonly AdminPageGate[] = Object.entries(accessRules).map(
  ([path, rule]) => ({ id: pageDir(path), ...rule }),
);

/** Для тесту реєстру й `catalog.ts`: сторінки платформи в збірці (доступ), у порядку тек. */
export const platformAdminPages = () => PLATFORM_PAGES;

/** Модулі, увімкнені для людини (мають слот `adminPages`, якщо він є). */
export const enabledModules = (
  meta: AppMeta,
  manifests: readonly WebModuleManifest[] = WEB_MODULES,
) => manifests.filter((m) => meta.modules.includes(m.id));

/** Чи дає людині сторінку її право: одне право, будь-яке з переліку чи перевірка за `/meta`. */
export function allowedBy(meta: AppMeta, permission: AdminPageAccess): boolean {
  if (typeof permission === 'function') return permission(meta);
  const any = typeof permission === 'string' ? [permission] : permission;
  return any.some((key) => meta.permissions.includes(key));
}

/**
 * Сторінки, доступні людині, у вхідному порядку. Повтор id (модуль не може перекрити сторінку
 * платформи) — береться перша: якщо їй не дали права, друга з тим самим id теж прихована.
 */
export function allowedPages<T extends AdminPageGate>(meta: AppMeta, pages: readonly T[]): T[] {
  const seen = new Set<string>();
  return pages.filter((page) => {
    if (seen.has(page.id) || !ADMIN_GROUPS.includes(page.group)) return false;
    seen.add(page.id);
    return allowedBy(meta, page.permission);
  });
}

/** Сторінка «Довідники» (`pages/references`): її ведуть і люди без решти адмінки. */
export const REFERENCES_PAGE = 'references';

/** Сторінки адмінки, доступні людині (платформа й увімкнені модулі). */
const reachablePages = (meta: AppMeta) =>
  allowedPages(meta, [
    ...PLATFORM_PAGES,
    ...enabledModules(meta).flatMap((m) => m.adminPages ?? []),
  ]);

/** Єдина доступна сторінка — «Довідники»: розділ адмінки з меню їй не потрібен. */
export const onlyReferences = (pages: readonly { id: string }[]) =>
  pages.length === 1 && pages[0]?.id === REFERENCES_PAGE;

/**
 * Що показати в навігації: `section` — «Налаштування» (є хоч одна сторінка, крім довідників),
 * `references` — лише «Довідники» (людина веде довідник без адмін-прав), `none` — нічого.
 */
export function adminEntry(meta: AppMeta): 'none' | 'section' | 'references' {
  const pages = reachablePages(meta);
  if (pages.length === 0) return 'none';
  return onlyReferences(pages) ? 'references' : 'section';
}

/** Чи є в людини хоч одна сторінка адмінки: без цього розділу в навігації немає. */
export const hasAdmin = (meta: AppMeta) => adminEntry(meta) !== 'none';

/** Чи доступні людині «Довідники» (палітра шукає їх і тоді, коли пункт навігації — «Адмінка»). */
export const hasReferences = (meta: AppMeta) =>
  reachablePages(meta).some((page) => page.id === REFERENCES_PAGE);
